import { Icon } from '@components/icon/Icon';
import { reportAppError } from '@shared/reportAppError';
import { RestaurantStatus } from '@shared/types/restaurants';
import { ISOTimeTo12HourTime } from '@shared/utils/time';
import typography from '~styles/typography.scss';
import { useAvailabilityContext } from '../context/AvailabilityContext';
import { useRestaurantContext } from '../context/RestaurantContext';
import { AvailabilityItem } from './AvailabilityItem';
import styles from './AvailabilityList.scss';
import { ComingSoon } from './ComingSoon';
import { NoAvailabilities } from './NoAvailabilities';

export const AvailabilityList = () => {
  const {
    filteredAvailabilities,
    hasUnsupportedGuestCount,
    isAvailabilitiesFetchLoading,
    selectedFloorPlanListingIds,
    selectedTime,
    selectedDate,
  } = useAvailabilityContext();
  const { restaurantDetails } = useRestaurantContext();

  if (isAvailabilitiesFetchLoading) return null;

  const userHasSelectedAListingOnFloorPlan = Boolean(
    selectedFloorPlanListingIds.length,
  );

  const noAvailabilitiesAreBuyable = filteredAvailabilities.every(
    (a) => !a.isBuyable,
  );

  const renderList = () => {
    if (restaurantDetails.status === RestaurantStatus.ComingSoon) {
      return <ComingSoon />;
    }

    if (hasUnsupportedGuestCount) {
      return (
        <NoAvailabilities iconName="couple" title="Unsupported Guest Count">
          <p>Adjust your guest count to try again</p>
        </NoAvailabilities>
      );
    }

    if (!filteredAvailabilities.length) {
      return (
        <NoAvailabilities iconName="table" title="No Tables Available">
          <p>
            Try adjusting your date, time, guest count, or floor plan selection
          </p>
        </NoAvailabilities>
      );
    }

    // Best available availability (cheapest) should always be present but it is
    // not guaranteed to be present.
    const bestAvailableAvailability = filteredAvailabilities.find(
      (availability) => availability.isBestAvailable,
    );

    // Featured upgrade availability (most expensive) is not guaranteed
    // to be present. It requires a listing with price > $0.
    const featuredUpgradeAvailability = filteredAvailabilities.find(
      (availability) => availability.isFeaturedUpgrade,
    );

    if (!userHasSelectedAListingOnFloorPlan && !bestAvailableAvailability) {
      reportAppError(
        new Error(
          `No best available availability found for restaurant ${restaurantDetails.id} on ${selectedDate} @ ${selectedTime}`,
        ),
      );

      return (
        <NoAvailabilities iconName="table" title="">
          <p>Select a table on the floor plan to see what's available.</p>
        </NoAvailabilities>
      );
    }

    return (
      <div className={styles.availabilitiesContainer}>
        {userHasSelectedAListingOnFloorPlan && (
          <ul className={styles.list} aria-label="Restaurant Availabilities">
            {filteredAvailabilities.map((availability) => (
              <AvailabilityItem
                key={availability.listing.id}
                availability={availability}
              />
            ))}
          </ul>
        )}
        {!userHasSelectedAListingOnFloorPlan && (
          <>
            {noAvailabilitiesAreBuyable && (
              <div className={styles.offerInstructions}>
                <h6 className={typography.t3_12}>
                  All {ISOTimeTo12HourTime(selectedTime as string)} tables are
                  reserved
                </h6>
                <div className={styles.iconRow}>
                  <Icon name="offer" />
                  <p className={typography.h7m}>
                    Offer to buy a reservation from someone else.
                  </p>
                </div>
              </div>
            )}

            <ul
              className={styles.list}
              aria-label="Best Available Availability"
            >
              <AvailabilityItem availability={bestAvailableAvailability!} />
            </ul>

            <p className={styles.message}>
              Click the floorplan to choose your own table.
            </p>

            {featuredUpgradeAvailability && (
              <>
                <h4 className={styles.featuredUpgradesHeader}>
                  Featured Upgrades
                </h4>
                <ul className={styles.list} aria-label="Featured upgrades">
                  <AvailabilityItem
                    availability={{
                      ...featuredUpgradeAvailability,
                      isBestAvailable: false,
                    }}
                  />
                </ul>
              </>
            )}
          </>
        )}
      </div>
    );
  };

  return <div data-testid="availability-container">{renderList()}</div>;
};
